import { TYPES as APPSTATE_TYPES } from '@monofe/appstate';
import type { IContainer } from '@monofe/di';
import { buildBindable } from '@monofe/di';
import type { ILog } from '@monofe/util';
import { injectLog, injectTranslationStorage, TYPES as UTIL_TYPES } from '@monofe/util';
import { injectToast, TYPES as TOAST_TYPES } from '../toast/interface';
import type { IOfflineAlert } from './interface';
import { bindOfflineAlert, injectOfflineAlert, TYPES, unbindOfflineAlert } from './interface';
import state from './state';
import events from './util/events';
import type { IModuleToast } from './util/toast';
import toast from './util/toast';
import type { IModuleTranslate } from './util/translate';
import translate from './util/translate';

export interface IModuleDependencies {
    toast: Readonly<IModuleToast>;
    translate: Readonly<IModuleTranslate>;
    log: Readonly<ILog>;
}

function _buildLocalDependencies(
    container: IContainer,
    dependencies: Partial<IModuleDependencies>,
): Promise<IModuleDependencies> {
    const translationStorage = injectTranslationStorage(container);
    const log = injectLog(container);
    const toastModule = injectToast(container);

    const translater = dependencies.translate ?? translate(translationStorage);
    const toaster = dependencies.toast ?? toast(translater, toastModule);

    return Promise.resolve({
        toast: toaster,
        translate: translater,
        log,
    });
}

function _buildModule(deps: IModuleDependencies): IOfflineAlert {
    const callback = () => {
        const isOnline = navigator.onLine;

        const shouldShow = !isOnline;

        if (state.isShowing() !== shouldShow) {
            shouldShow ? state.show() : state.hide();
        }
    };

    /* Remove duplicate callbacks & (re)subscribe */
    events.unsubscribeOnlineStatusChange(callback);
    events.subscribeOnlineStatusChange(callback);

    return {
        /* eslint-disable-next-line react/jsx-no-useless-fragment */
        renderer: () => <></>,
        show: () => state.show(),
        hide: () => state.hide(),
        isShowing: () => state.isShowing(),
    };
}

export async function binder(
    container: IContainer,
    dependencies: Partial<IModuleDependencies>,
): Promise<Readonly<IOfflineAlert>> {
    const deps = await _buildLocalDependencies(container, dependencies);

    const module = _buildModule(deps);

    return Object.freeze(module);
}

export default buildBindable<IOfflineAlert>({
    id: TYPES.IOfflineAlert,
    dependencies: [
        TOAST_TYPES.IToast,
        UTIL_TYPES.ITranslationStorage,
        UTIL_TYPES.ILog,
        APPSTATE_TYPES.IStoresReducer,
    ],
    bind: bindOfflineAlert,
    remove: unbindOfflineAlert,
    inject: injectOfflineAlert,
    build: container => binder(container, {}),
});
